// Non-map render states: skeleton, network error (retryable), empty locator, and
// the entitlement UNAVAILABLE state.
//
// HOUSE RULE — human copy, visible failure over silent fallback: a failed fetch
// shows a retry affordance, never a blank map; an empty locator says so plainly;
// an unavailable locator gets a calm, human message, never a stack trace or a
// broken page. All copy here is finished, human-facing English (no placeholders).

/** Builds the async-boot skeleton that occupies the reserved box (no layout shift). */
export function skeletonState(): HTMLElement {
  const wrap = el("div", "skeleton");
  const list = el("div", "skeleton-list");
  for (let i = 0; i < 6; i++) {
    list.appendChild(el("div", "skeleton-row"));
  }
  wrap.appendChild(list);
  wrap.appendChild(el("div", "skeleton-map"));
  return wrap;
}

/**
 * A retryable failure state. [onRetry] is wired to a real button so a transient
 * network / 5xx / 429 problem is recoverable in place — the map never silently
 * blanks. When [retryAfterSeconds] is set (server `Retry-After` on a 429), the
 * button is held disabled with a live countdown so a retry can't immediately
 * re-trip the rate limiter.
 */
export function errorState(message: string, onRetry: () => void, retryAfterSeconds: number | null = null): HTMLElement {
  const wrap = el("div", "state");
  wrap.setAttribute("role", "alert");
  wrap.appendChild(text("h2", "state-title", "We couldn't load the store locator"));
  wrap.appendChild(text("p", "state-body", message));
  const button = text("button", "state-action", "Try again") as HTMLButtonElement;
  button.type = "button";
  button.addEventListener("click", onRetry);
  wrap.appendChild(button);

  if (retryAfterSeconds != null && retryAfterSeconds > 0) {
    startRetryCountdown(button, retryAfterSeconds);
  }
  return wrap;
}

/**
 * Disables [button] with a "Try again in Ns" countdown, re-enabling at zero. Exported so the
 * inline (in-directory) retry honors a 429's Retry-After exactly like the full-pane one — two
 * countdowns would be two chances to get the rate-limit contract wrong.
 */
export function startRetryCountdown(button: HTMLButtonElement, seconds: number): void {
  let remaining = seconds;
  button.disabled = true;
  button.textContent = `Try again in ${remaining}s`;
  const timer = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      clearInterval(timer);
      button.disabled = false;
      button.textContent = "Try again";
      return;
    }
    button.textContent = `Try again in ${remaining}s`;
    // Stop the timer if the button leaves the DOM (state replaced) to avoid a leak.
    if (!button.isConnected) {
      clearInterval(timer);
    }
  }, 1000);
}

/**
 * The MAP PANE's own failure state — the map chunk (MapLibre) could not be loaded or constructed.
 *
 * Scoped to the pane on purpose (F-030 progressive enhancement): the directory is fetched and
 * rendered independently of the map, so a map failure leaves a fully usable store locator — the
 * visitor can still search, filter, read distances, open a location and get directions. Replacing
 * all of that with a full-pane error would destroy working UI over a degraded enhancement. The
 * copy names what is missing rather than implying the locator is broken, and [onRetry] retries the
 * map alone.
 */
export function mapErrorState(onRetry: () => void): HTMLElement {
  const wrap = el("div", "map-error");
  wrap.setAttribute("role", "status");
  wrap.appendChild(text("p", "map-error-text", "The map couldn't be loaded. The list of locations still works."));
  const button = text("button", "list-widen-button", "Retry map") as HTMLButtonElement;
  button.type = "button";
  button.addEventListener("click", onRetry);
  wrap.appendChild(button);
  return wrap;
}

/** A terminal (non-retryable) failure, e.g. an unknown/rotated token (404). */
export function terminalErrorState(message: string): HTMLElement {
  const wrap = el("div", "state");
  wrap.setAttribute("role", "alert");
  wrap.appendChild(text("h2", "state-title", "Store locator unavailable"));
  wrap.appendChild(text("p", "state-body", message));
  return wrap;
}

/** An honest empty state: the locator loaded fine but has no locations to show. */
export function emptyState(locatorName: string): HTMLElement {
  const wrap = el("div", "state");
  wrap.appendChild(text("h2", "state-title", "No locations yet"));
  wrap.appendChild(
    text("p", "state-body", `${locatorName} hasn't published any locations to this map yet. Please check back soon.`),
  );
  return wrap;
}

/**
 * The entitlement UNAVAILABLE state (config.flags.availability). Deliberately
 * vague about the billing reason — the visitor is not the account owner and
 * shouldn't see "subscription lapsed" — and deliberately NOT phrased like
 * [errorState]: this is not a transient, retryable failure (there is no retry
 * button, no "try again" framing), it is the account's actual current state,
 * so the copy stays calm and neutral rather than implying the visitor caused
 * or can fix it (PM epic-007 follow-up #2).
 */
export function unavailableState(): HTMLElement {
  const wrap = el("div", "state");
  wrap.appendChild(text("h2", "state-title", "Store locator unavailable"));
  wrap.appendChild(text("p", "state-body", "This store locator isn't available right now."));
  return wrap;
}

function el(tag: string, className: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}

function text(tag: string, className: string, content: string): HTMLElement {
  const node = el(tag, className);
  node.textContent = content;
  return node;
}
